import { useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyReturn, ArrowLeft, CheckCircle, Warning } from '@phosphor-icons/react';
import api from '../lib/api';

/**
 * Public self-service page for customers who lost their license-delivery
 * email. They enter the email they paid with; if we find any active
 * licenses tied to it, the backend re-sends them via Resend.
 *
 * We DELIBERATELY return the same friendly message whether or not we
 * found a match — prevents email-address enumeration.
 */
export default function RecoverLicensePage() {
  const [email, setEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { ok, message } | { error }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await api.post('/migrate-pro/recover-license', {
        email: email.trim(),
        company_website: companyWebsite,
      });
      setResult({ ok: true, message: res.data?.message || 'Check your inbox.' });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setResult({ error: detail || 'Too many requests. Please wait a few minutes and try again.' });
      } else {
        setResult({ error: 'Something went wrong. Please try again in a moment.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0F0F1A] text-white flex flex-col"
      data-testid="recover-license-page"
    >
      {/* Simple header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link
            to="/migrate"
            className="text-xl font-black tracking-tight hover:opacity-80"
            data-testid="recover-brand-link"
          >
            Migration<span className="text-[#F39C12]">Crunch</span>
          </Link>
          <Link
            to="/migrate"
            className="text-sm text-white/60 hover:text-white flex items-center gap-1"
            data-testid="recover-back-link"
          >
            <ArrowLeft size={14} weight="bold" />
            Back to MigrationCrunch
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 py-12">
        <div className="max-w-xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F39C12]/15 mb-5">
              <KeyReturn size={26} weight="fill" className="text-[#F39C12]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">
              Recover your license key
            </h1>
            <p className="text-white/60 text-base leading-relaxed">
              Lost the email with your MigrationCrunch license key? Enter the
              address you paid with and we&rsquo;ll resend every active license
              tied to it &mdash; instantly, no support ticket required.
            </p>
          </div>

          {/* Card */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 sm:p-8">
            {result?.ok ? (
              <div
                className="text-center py-4"
                data-testid="recover-success"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/20 mb-4">
                  <CheckCircle size={28} weight="fill" className="text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold mb-2">Check your inbox</h2>
                <p className="text-white/60 text-sm leading-relaxed mb-6">
                  {result.message}
                </p>
                <p className="text-white/40 text-xs mb-6">
                  Give it a minute or two &mdash; and check your spam folder
                  just in case. Nothing arrives after 10 minutes? Just email{' '}
                  <a
                    href="mailto:chip@migrationcrunch.com"
                    className="text-[#F39C12] hover:underline"
                  >
                    chip@migrationcrunch.com
                  </a>{' '}
                  and Chip will sort it manually.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setResult(null);
                    setEmail('');
                  }}
                  className="text-sm text-white/60 hover:text-white underline"
                  data-testid="recover-try-another-btn"
                >
                  Try another email
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="recover-form">
                <label
                  htmlFor="recover-email"
                  className="block text-xs font-bold uppercase tracking-widest text-white/60 mb-2"
                >
                  Email address you paid with
                </label>
                <input
                  id="recover-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full bg-[#0F0F1A] border border-white/20 rounded px-4 py-3 text-white placeholder:text-white/30 focus:border-[#F39C12] focus:outline-none"
                  data-testid="recover-email-input"
                  disabled={submitting}
                />

                {/* Honeypot — bots fill hidden inputs. Real users never see it. */}
                <input
                  type="text"
                  name="company_website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                  }}
                  aria-hidden="true"
                />

                {result?.error && (
                  <div
                    className="mt-4 flex items-start gap-2 bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 rounded text-sm"
                    data-testid="recover-error"
                  >
                    <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                    <span>{result.error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="mt-5 w-full bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold px-6 py-3 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="recover-submit-btn"
                >
                  {submitting ? 'Looking up…' : 'Email me my license key'}
                </button>

                <p className="mt-4 text-xs text-white/40 leading-relaxed">
                  We&rsquo;ll always return a success message even if we
                  don&rsquo;t find a match &mdash; that&rsquo;s deliberate, to
                  keep bad actors from probing which emails have licenses. If
                  you don&rsquo;t receive an email within a few minutes,
                  double-check the address you paid with (the one on your
                  Stripe receipt).
                </p>
              </form>
            )}
          </div>

          {/* Escape hatch */}
          <div className="mt-8 text-center text-sm text-white/50">
            Never bought yet?{' '}
            <Link
              to="/migrate"
              className="text-[#F39C12] hover:underline"
              data-testid="recover-buy-link"
            >
              Start a free 50-contact preview →
            </Link>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 py-6 px-6 text-center text-xs text-white/40">
        MigrationCrunch · a SalesCrunchCRM product ·{' '}
        <a
          href="mailto:chip@migrationcrunch.com"
          className="hover:text-white"
        >
          chip@migrationcrunch.com
        </a>
      </footer>
    </div>
  );
}
